export type DecimalValue = Readonly<{ coefficient: string; scale: number }>;
export type RoundingMode = "halfAwayFromZero";
const maxScale = 1000;
const maxDigits = 1024;

function validateScale(scale: number) {
  if (!Number.isInteger(scale) || scale < 0 || scale > maxScale)
    throw new Error(`Scale must be a non-negative integer no greater than ${maxScale}`);
}
function validate(value: DecimalValue): DecimalValue {
  validateScale(value.scale);
  if (typeof value.coefficient !== "string" || value.coefficient.trim() !== value.coefficient || !/^-?(0|[1-9]\d*)$/.test(value.coefficient)
    || value.coefficient.replace("-", "").length > maxDigits)
    throw new Error("Invalid decimal coefficient");
  return { coefficient: BigInt(value.coefficient).toString(), scale: value.scale };
}
function parse(text: string): DecimalValue {
  const match = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/.exec(text.trim());
  if (!match) throw new Error("Invalid decimal text");
  const fraction = match[3] ?? match[4] ?? "";
  const exponent = Number(match[5] ?? 0);
  if (!Number.isInteger(exponent) || Math.abs(exponent) > maxScale) throw new Error("Unsupported decimal exponent");
  const scale = fraction.length - exponent;
  const digits = `${match[2] ?? "0"}${fraction}`.replace(/^0+(?=\d)/, "");
  if (digits.length > maxDigits) throw new Error("Invalid decimal coefficient");
  const coefficient = BigInt(`${match[1] === "-" ? "-" : ""}${digits}`) * 10n ** BigInt(Math.max(0, -scale));
  return validate({ coefficient: coefficient.toString(), scale: Math.max(0, scale) });
}
function toText(input: DecimalValue): string {
  const value = validate(input);
  const negative = value.coefficient.startsWith("-");
  const digits = value.coefficient.replace("-", "").padStart(value.scale + 1, "0");
  const text = value.scale ? `${digits.slice(0, -value.scale)}.${digits.slice(-value.scale)}` : digits;
  return `${negative ? "-" : ""}${text}`;
}
function fraction(value: DecimalValue): [bigint, bigint] {
  const valid = validate(value);
  return [BigInt(valid.coefficient), 10n ** BigInt(valid.scale)];
}

/** Exact intermediate result. Finish explicitly with toDecimal() or round(). */
export class DecimalCalculation {
  private readonly numerator: bigint;
  private readonly denominator: bigint;

  private constructor(numerator: bigint, denominator: bigint) {
    let divisor = numerator < 0n ? -numerator : numerator;
    let remainder = denominator;
    while (remainder) [divisor, remainder] = [remainder, divisor % remainder];
    this.numerator = numerator / divisor;
    this.denominator = denominator / divisor;
  }
  static from(value: DecimalValue): DecimalCalculation {
    return new DecimalCalculation(...fraction(value));
  }
  add(value: DecimalValue): DecimalCalculation {
    const [n, d] = fraction(value);
    return new DecimalCalculation(this.numerator * d + n * this.denominator, this.denominator * d);
  }
  subtract(value: DecimalValue): DecimalCalculation {
    const [n, d] = fraction(value);
    return new DecimalCalculation(this.numerator * d - n * this.denominator, this.denominator * d);
  }
  multiply(value: DecimalValue): DecimalCalculation {
    const [n, d] = fraction(value);
    return new DecimalCalculation(this.numerator * n, this.denominator * d);
  }
  divide(value: DecimalValue): DecimalCalculation {
    const [n, d] = fraction(value);
    if (n === 0n) throw new Error("Decimal calculations cannot divide by zero");
    const sign = n < 0n ? -1n : 1n;
    return new DecimalCalculation(this.numerator * d * sign, this.denominator * n * sign);
  }
  round(scale: number, mode: RoundingMode): DecimalValue {
    validateScale(scale);
    if (mode !== "halfAwayFromZero") throw new Error("Unsupported rounding mode");
    const negative = this.numerator < 0n;
    const scaled = (negative ? -this.numerator : this.numerator) * 10n ** BigInt(scale);
    let coefficient = scaled / this.denominator;
    if ((scaled % this.denominator) * 2n >= this.denominator) coefficient++;
    return validate({ coefficient: (negative ? -coefficient : coefficient).toString(), scale });
  }
  toDecimal(): DecimalValue {
    const n = this.numerator;
    let d = this.denominator;
    let twos = 0, fives = 0;
    while (d % 2n === 0n) { d /= 2n; twos++; }
    while (d % 5n === 0n) { d /= 5n; fives++; }
    if (d !== 1n) throw new Error("Repeating decimal requires explicit rounding");
    const scale = Math.max(twos, fives);
    validateScale(scale);
    return validate({ coefficient: (n * 2n ** BigInt(scale - twos) * 5n ** BigInt(scale - fives)).toString(), scale });
  }
  /** Compatibility boundary only; authoritative consumers must not use this result. */
  toApproximateNumber(): number {
    const value = Number(this.numerator) / Number(this.denominator);
    if (!Number.isFinite(value)) throw new Error("Decimal calculation exceeds finite number precision");
    return value;
  }
}

function combine(left: DecimalValue, right: DecimalValue, sign: bigint): DecimalValue {
  const a = validate(left), b = validate(right);
  const scale = Math.max(a.scale, b.scale);
  const coefficient = BigInt(a.coefficient) * 10n ** BigInt(scale - a.scale)
    + sign * BigInt(b.coefficient) * 10n ** BigInt(scale - b.scale);
  return validate({ coefficient: coefficient.toString(), scale });
}

export const Decimal = {
  validate, parse, toText,
  calculate: DecimalCalculation.from,
  normalize: (value: DecimalValue) => DecimalCalculation.from(value).toDecimal(),
  add: (left: DecimalValue, right: DecimalValue): DecimalValue => combine(left, right, 1n),
  subtract: (left: DecimalValue, right: DecimalValue): DecimalValue => combine(left, right, -1n),
  compare: (left: DecimalValue, right: DecimalValue): -1 | 0 | 1 => {
    const [ln, ld] = fraction(left), [rn, rd] = fraction(right);
    const difference = ln * rd - rn * ld;
    return difference < 0n ? -1 : difference > 0n ? 1 : 0;
  },
  equals: (left: DecimalValue, right: DecimalValue) => Decimal.compare(left, right) === 0
};
